import { Table } from "antd";
import React from "react";

// Helper to get value for a parameter and groupNames
function getZoneValues(param, groupNames) {
  if (!param || !Array.isArray(param.value)) return groupNames.map(() => "");
  return groupNames.map((g) => {
    const found = param.value.find((v) => v.groupName === g);
    return found !== undefined && found !== null ? found.value : "";
  });
}

// Helper to get a single value (for pipe-level params)
function getPipeValue(param) {
  if (!param || !Array.isArray(param.value)) return [""];
  return [param.value[0]?.value ?? ""];
}

// Main mapping function: fully dynamic columns and data
function mapPipeParameterDataToTable(pipeParameterData) {
  if (
    !pipeParameterData ||
    !pipeParameterData.values ||
    !pipeParameterData.headers
  )
    return {
      tableData: [],
      columnGroups: [],
      groupNameMap: {},
      totalDataColumns: 0,
      passZoneCounts: {},
    };
  const { values, headers } = pipeParameterData;

  // Dynamically build columnGroups and groupName mapping from headers
  const columnGroups = [];
  const groupNameMap = {};
  let totalDataColumns = 0;
  const passZoneCounts = {};
  Object.entries(headers).forEach(([passKey, passObj]) => {
    const subColumns = [];
    const groupNames = [];
    Object.entries(passObj.zones).forEach(([zoneKey, zoneObj]) => {
      subColumns.push(zoneObj.name);
      groupNames.push(zoneKey);
    });
    columnGroups.push({
      title: passObj.name,
      key: passKey,
      subColumns,
    });
    groupNameMap[passKey] = groupNames;
    passZoneCounts[passKey] = subColumns.length;
    totalDataColumns += subColumns.length;
  });

  // Build tableData rows
  const tableData = [];
  let keyCounter = 1;
  Object.entries(values).forEach(([sectionName, sectionObj]) => {
    // Insert section header
    tableData.push({
      key: `header-${sectionName}`,
      parameter: sectionName
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase()),
      isHeader: true,
      ...Object.fromEntries(
        Object.keys(groupNameMap).map((passKey) => [
          passKey,
          groupNameMap[passKey].map(() => ""),
        ])
      ),
    });
    // For each parameter in the section
    Object.entries(sectionObj).forEach(([paramKey, paramObj]) => {
      const row = {
        key: `${keyCounter++}`,
        parameter: `${paramObj.text || paramKey} ${
          paramObj?.units?.imperial ? `(${paramObj?.units?.imperial})` : ""
        }`,
        section: sectionName,
      };
      Object.entries(groupNameMap).forEach(([passKey, groupNames]) => {
        row[passKey] = getZoneValues(paramObj, groupNames);
      });
      // If all values are empty, try pipe-level (single value)
      const allEmpty = Object.values(groupNameMap).every(() =>
        row[Object.keys(groupNameMap)[0]].every((v) => v === "")
      );
      if (allEmpty) {
        Object.keys(groupNameMap).forEach((passKey) => {
          row[passKey] = getPipeValue(paramObj);
        });
      }
      row._paramObj = paramObj; // for custom rendering logic
      tableData.push(row);
    });
  });

  return {
    tableData,
    columnGroups,
    groupNameMap,
    totalDataColumns,
    passZoneCounts,
  };
}

const PipeParametersTable = ({ pipeParameterData }) => {
  // Filter all value arrays by groupName prefix before mapping
  const filterValuesByGroupName = (data) => {
    if (!data || !data.values || !data.groupName) return data;
    const groupPrefix = data.groupName;
    const filteredValues = {};
    Object.entries(data.values).forEach(([section, params]) => {
      filteredValues[section] = {};
      Object.entries(params).forEach(([paramKey, paramObj]) => {
        if (Array.isArray(paramObj.value)) {
          filteredValues[section][paramKey] = {
            ...paramObj,
            value: paramObj.value.filter(
              (v) =>
                typeof v.groupName === "string" &&
                v.groupName.startsWith(groupPrefix)
            ),
          };
        } else {
          filteredValues[section][paramKey] = paramObj;
        }
      });
    });
    return { ...data, values: filteredValues };
  };

  const filteredPipeParameterData = filterValuesByGroupName(pipeParameterData);

  const {
    tableData,
    columnGroups,
    groupNameMap,
    totalDataColumns,
    passZoneCounts,
  } = mapPipeParameterDataToTable(filteredPipeParameterData);

  // Helper function for custom colSpan logic
  const getColSpan = (record, groupKey, zoneIdx) => {
    if (record.isHeader) return { colSpan: 0 };
    // Pipe and Power Supply: single value, span all columns
    if (record.section === "Pipe" || record.section === "Power Supply") {
      if (groupKey === Object.keys(groupNameMap)[0] && zoneIdx === 0) {
        return { colSpan: totalDataColumns, value: record[groupKey][0] };
      } else {
        return { colSpan: 0, value: undefined };
      }
    }
    // Pass: one value per pass, span all zones in that pass
    if (record.section === "Pass") {
      if (zoneIdx === 0) {
        return {
          colSpan: passZoneCounts[groupKey],
          value: record[groupKey][0],
        };
      } else {
        return { colSpan: 0, value: undefined };
      }
    }
    // Default: per-zone value
    return { colSpan: 1, value: record[groupKey][zoneIdx] };
  };

  // Factory function to create position columns
  const createPositionColumns = (group) => {
    return {
      title: (
        <div className="pipe-parameters-table-section-header">
          {group.title}
        </div>
      ),
      children: group.subColumns.map((subCol, index) => ({
        title: (
          <div className="pipe-parameters-table-section-header">{subCol}</div>
        ),
        dataIndex: [group.key, index],
        key: `${group.key}${subCol.replace(/\s+/g, "")}`,
        width: 150,
        align: "center",
        render: (value, record) => {
          const { value: cellValue } = getColSpan(record, group.key, index);
          return cellValue;
        },
        onCell: (record) => {
          if (record.isHeader) return { colSpan: 0 };
          return getColSpan(record, group.key, index);
        },
      })),
    };
  };

  // Columns configuration
  const columns = [
    {
      title: "",
      dataIndex: "parameter",
      key: "parameter",
      fixed: "left",
      width: 200,
      className: "parameter-column",
      render: (text, record) => (
        <div
          className={
            record.isHeader
              ? "parameter-column-text-header"
              : "parameter-column-text-normal"
          }
        >
          {text}
        </div>
      ),
      onCell: (record) => {
        if (record.isHeader) {
          // Section/category header: span all columns
          return { colSpan: 1 + totalDataColumns };
        }
        return {};
      },
    },
    ...columnGroups.map((group) => createPositionColumns(group)),
  ];

  const calcHeight = -240;
  const tableHeight = `calc(${calcHeight}px + 100vh)`;

  return (
    <div
      className="pipe-parameters-table-container"
      style={{
        height: tableHeight,
        position: "relative",
      }}
    >
      <Table
        columns={columns}
        dataSource={tableData}
        bordered
        pagination={false}
        className="pipe-parameters-table"
        scroll={{ x: "max-content", y: tableHeight }}
      />
    </div>
  );
};

export default PipeParametersTable;
